import { AttachMoney } from '@mui/icons-material';
import { FormControl, FormHelperText, FormLabel, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { HourlyLineItemDto, OrderDto, QuoteDto, RegionDto } from '../../../../../dtos';
import {
    useArchiveHourlyLineItemDeleteFromDatabaseByIdMutation,
    useGetLineItemDoesHaulingLineItemHaveCorrespondingMaterialLineItemsQuery,
} from '../../../../../store/generated/generatedApi';
import { LineItemTypes, hourlyTypesList } from '../../../../../util';
import { EquipmentTypeSelect, SiteSelect, AddressForm, PoAndJobNumberInput } from '../../../../CommonInputs';
import { SlimFormHeader } from '../../../../Core/SlimFormHeader';
import { FormInput, FormNumberInput, FormSection, IFormProps, LoadingIndicator } from '../../../../CoreLib/library';
import { useHourlyLineItemForm } from './useHourlyLineItemForm';
import { MaterialBilledToSelect } from '../../../../CommonInputs/MaterialBilledToSelect';
import { AddressLookup } from '../../../../CommonInputs/AddressLookup';
import { BilledToType, BilledToTypeToText } from '../../../../../dtos/enums/BilledToType';
import { IEntityAutocomplete } from '../../../../CommonInputs/IEntityAutocomplete';

export interface IHourlyLineItemFormProps extends IFormProps<HourlyLineItemDto> {
    quote?: QuoteDto;
    order?: OrderDto;
}

export const HourlyLineItemForm: FC<IHourlyLineItemFormProps> = (props) => {
    const { isLoading, quote, order } = props;
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleSiteChange,
        handleSiteNameChange,
        handleQuantityChange,
        handleRateChange,
        handleDriverRateChange,
        handleBrokerRateChange,
        handleHourlyTypeChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleCustomerJobNumberChange,
        handleZoneChange,
        handleIsBilledToChange,
        handleBilledToChange,
        handlePoNumberChange,
        handleFormAddressChange,
        handleFormRegionChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        formQuote,
        formEquipmentType,
        formSite,
        formSiteName,
        formQuantity,
        formRate,
        formDriverRate,
        formBrokerRate,
        formIsActive,
        formHourlyType,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formZone,
        formIsBilledTo,
        formBilledTo,
        formPoNumber,
        formAddressLine1,
        formAddressLine2,
        formCountry,
        formDirections,
        formCity,
        formState,
        formZipCode,
        regions,
        formRegion,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formTotal
    } = useHourlyLineItemForm(props, quote);

    const {
        data: hasCorrespondingMaterialLineItems,
        error: canDeleteMaterialError,
        isLoading: canDeleteMaterialLoading,
    } = useGetLineItemDoesHaulingLineItemHaveCorrespondingMaterialLineItemsQuery({ id: props.initValues?.id ?? '', lineItemType: LineItemTypes.Hourly });

    const [canDelete, setCanDelete] = useState(true);
    useEffect(() => {
        if (canDeleteMaterialError) {
            if ((canDeleteMaterialError as any).status === 404) {
                setCanDelete(true);
            }
        }

        if (hasCorrespondingMaterialLineItems && hasCorrespondingMaterialLineItems === true) {
            setCanDelete(false);
        }
    }, [canDeleteMaterialError, hasCorrespondingMaterialLineItems]);

    useEffect(() => {
        if (quote && !formQuote) {
            handleQuoteChange(quote);
        }
    }, [formQuote, handleQuoteChange, quote]);

    const currentHourlyLineItemId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    if (isLoading || canDeleteMaterialLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Grid container direction='column'>
            <SlimFormHeader
                objectName='Hourly Line Item'
                permissionsTypeName={'lineItem'}
                icon={<AttachMoney />}
                title={formQuote ? `Hourly Line Item for Quote #${formQuote.quoteNumber}` : 'New Hourly Line Item'}
                titleMaxWidth="60vw"
                breadcrumbs={
                    order
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Jobs', navLink: '/jobs' },
                            { label: `Job #${quote?.quoteNumber}`, navLink: `/job/${order?.jobId}` },
                            { label: `Order #${order?.orderNumber}`, navLink: `/job/${order?.jobId}/order/${order?.id}` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: 'Quotes', navLink: '/quotes' },
                            { label: `${quote?.jobName ? `${quote.jobName} - ` : ''}${quote?.customer?.customerNumber ? `${quote.customer.customerNumber}: ` : ''}${quote?.customer?.name ?? ''} (Quote #${quote?.quoteNumber ?? 'Quote'})`, navLink: `/quote/${quote?.id}` },
                            { label: `Line Items`, navLink: `/quote/${quote?.id}/lineItems` },
                        ]
                }
                isActive={formIsActive}
                id={currentHourlyLineItemId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'hourly line item'}
                canDelete={canDelete}
                canSave={true}
                deleteMutation={useArchiveHourlyLineItemDeleteFromDatabaseByIdMutation}
            />
            <Grid item mx={2}>
                <FormSection>
                    <Grid item>
                        <Typography variant='h2'>General</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                            <FormControl error={!!fieldErrors.get('hourlyType')} fullWidth required disabled={!canDelete}>
                                <FormLabel>Type</FormLabel>
                                <Select value={formHourlyType ?? ''} onChange={handleHourlyTypeChange}>
                                    {hourlyTypesList.map((unit) => {
                                        return (
                                            <MenuItem key={unit} value={unit}>
                                                {unit}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <FormHelperText>{fieldErrors.get('hourlyType')}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                            <EquipmentTypeSelect
                                selectedEquipmentTypeId={formEquipmentType?.id ?? ''}
                                handleSelectedEquipmentTypeChange={handleEquipmentTypeChange}
                                required
                                errorMessage={fieldErrors.get('equipmentTypeId')}
                                disabled={!canDelete}
                                hourlyType={formHourlyType}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} md={2} lg={2} xl={2}>
                            <FormInput value={formZone} onChange={handleZoneChange} label='Zone' fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={2} md={2} xl={2}>
                            <MaterialBilledToSelect
                                selectedBilledTo={formIsBilledTo}
                                handleSelectedBilledToChange={handleIsBilledToChange}
                                required
                                errorMessage={fieldErrors.get('isBilledTo')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} md={2} xl={2}>
                            <FormInput
                                label='Material Bill To'
                                value={BilledToTypeToText(formIsBilledTo, quote?.customer?.name ?? '', formBilledTo)}
                                onChange={handleBilledToChange}
                                required={formIsBilledTo === BilledToType.Custom}
                                errorText={fieldErrors.get('billedTo')}
                                disabled={formIsBilledTo !== BilledToType.Custom}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PoAndJobNumberInput
                                formQuotePoNumber={formQuotePoNumber}
                                formIsCustomPoNumber={formIsCustomPoNumber}
                                handleIsCustomPoNumberChange={handleIsCustomPoNumberChange}
                                formPoNumber={formPoNumber}
                                formQuoteCustomerJobNumber={formQuoteCustomerJobNumber}
                                formIsCustomerJobNumberCustom={formIsCustomerJobNumberCustom}
                                formCustomerJobNumber={formCustomerJobNumber}
                                handleIsCustomerJobNumberCustomChange={handleIsCustomerJobNumberCustomChange}
                                handleCustomerJobNumberChange={handleCustomerJobNumberChange}
                                handlePoNumberChange={handlePoNumberChange}
                                fieldErrors={fieldErrors}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant='h2'>Pricing</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} sm={4} md={3} lg={2} xl={1}>
                            <FormNumberInput
                                value={formQuantity ?? ''}
                                onChange={handleQuantityChange}
                                label='Quantity'
                                name='quantity'
                                errorText={fieldErrors.get('quantity')}
                                error={!!fieldErrors.get('quantity')}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3} lg={2} xl={1}>
                            <FormNumberInput
                                value={formRate}
                                onChange={handleRateChange}
                                label='Hourly Rate'
                                name='rate'
                                errorText={fieldErrors.get('rate')}
                                error={!!fieldErrors.get('rate')}
                                fullWidth
                                required
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3} lg={2} xl={1}>
                            <FormNumberInput
                                value={formTotal ?? ''}
                                onChange={() => { }}
                                label='Total'
                                name='total'
                                fullWidth
                                disabled
                                isCurrency
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant='h2'>Haul To</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={3}>
                            <AddressLookup
                                handleAddressChange={handleFormAddressChange}
                                key={0}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <SiteSelect
                                label="Site Lookup"
                                selectedSiteId={formSite?.id ?? ''}
                                handleSelectedSiteChange={handleSiteChange}
                                errorMessage={fieldErrors.get('siteId')}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormInput
                                value={formSiteName ?? ''}
                                onChange={(event) => { handleSiteNameChange(event.target.value) }}
                                label='Site Name'
                                name='site'
                                fullWidth
                                disabled={isLoading || !!formSite}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl
                                fullWidth>
                                <FormLabel>Region</FormLabel>
                                <IEntityAutocomplete
                                    options={regions?.pageResults ?? []}
                                    onChange={(event, value) => handleFormRegionChange(value)}
                                    value={formRegion}
                                    getOptionLabel={(option: RegionDto) => option.name}
                                    disabled={isLoading}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item container direction={'row'} spacing={2} wrap="nowrap">
                            <AddressForm
                                handleAddressChange={handleFormAddressChange}
                                addressLine1={formAddressLine1}
                                addressLine2={formAddressLine2}
                                city={formCity}
                                state={formState}
                                zipCode={formZipCode}
                                country={formCountry}
                                directions={formDirections}
                                enableLookup={false}
                                disabled={isLoading}
                                allOptional
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} lg={2} xl={1}>
                            <FormNumberInput
                                value={formDriverRate}
                                onChange={handleDriverRateChange}
                                label='Driver Rate'
                                name='DriverRate'
                                errorText={fieldErrors.get('driverRate')}
                                error={!!fieldErrors.get('driverRate')}
                                fullWidth
                                required
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} lg={2} xl={1}>
                            <FormNumberInput
                                value={formBrokerRate}
                                onChange={handleBrokerRateChange}
                                label='Broker Rate'
                                name='BrokerRate'
                                errorText={fieldErrors.get('brokerRate')}
                                error={!!fieldErrors.get('brokerRate')}
                                fullWidth
                                disabled={isLoading}
                                required
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            value={formLineItemMemo ?? ''}
                            onChange={handleLineItemMemoChange}
                            label='Line Item Memo'
                            name='lineItemMemo'
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            value={formDriverMemo ?? ''}
                            onChange={handleDriverMemoChange}
                            label='Order Memo'
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Grid>
                </FormSection>
            </Grid>
        </Grid>
    );
};
